import { MouseEventHandler, useCallback, useEffect, useRef } from 'react';

/**
 * Options for useDelayedHover hook.
 */
interface UseDelayedHoverOptions {
    /**
     * Callback which is called to indicate whether the hover is in effect or not.
     */
    readonly onHover: (boolean) => void;

    /**
     * Delay after which the hover should be registered.
     */
    readonly delayInMs?: number;

    /**
     * Indicates whether the given delay should be used or not.
     */
    readonly delayActive?: boolean;
}

/**
 * The fields returned by the useDelayedHover hook.
 */
interface UseDelayedHover {
    /**
     * An onMouseEnter event handler that should be attached to an element.
     */
    onMouseEnter: MouseEventHandler<any>;

    /**
     * An onMouseLeave event handler that should be attached to an element.
     */
    onMouseLeave: MouseEventHandler<any>;

    /**
     * Can be used to cancel all currently active hover effects.
     */
    cancel: () => void;
}

/**
 * Provides a delayed hover effect based on onMouseEnter and onMouseLeave events. The hover is indicated after the
 * mouse remains on top of the element for longer than the given timeout.
 */
export function useDelayedHover({
    onHover,
    delayInMs = 100,
    delayActive = true
}: UseDelayedHoverOptions): UseDelayedHover {
    const startedRef = useRef(false);
    const timeoutRef = useRef<number | null>(null);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const onMouseEnter = useCallback(() => {
        if (!startedRef.current) {
            startedRef.current = true;

            if (timeoutRef.current !== null && timeoutRef.current !== undefined) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = window.setTimeout(
                () => {
                    if (startedRef.current) {
                        onHover(true);
                    }
                },
                delayActive ? delayInMs : 0
            );
        }
    }, [onHover, delayActive, delayInMs]);

    const onMouseLeave = useCallback(() => {
        startedRef.current = false;
        onHover(false);
    }, [onHover]);

    const cancel = useCallback(() => {
        startedRef.current = false;
        onHover(false);

        if (timeoutRef.current !== null && timeoutRef.current !== undefined) {
            clearTimeout(timeoutRef.current);
        }
    }, [onHover]);

    return {
        onMouseEnter,
        onMouseLeave,
        cancel
    };
}
